import { useMemo } from "react";
import classNames from "classnames";
import type ComponentTypes from "~/features/cms/componentTypes";
import { resolveUrl, isSiteRelativeUrl } from "~/utils/url/urlResolver";
import { Link, RouterLink } from "~/common/components/Link";
import { trackBannerClick } from "~/app/analytics/ga/contentStream";
import { cleanupText } from "~/features/cms/utils/markup";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { styled, FontSize, getFontStyle } from "@schaeffler-commerce/design-system";
import { C_ON_SURFACE, C_ON_SURFACE_VARIANT, C_PRIMARY, C_SURFACE } from "@schaeffler-commerce/design-system";
import type { JSX } from "react";

// Migrated from CMSBanner.less (.scha__banner).
const bannerRootStyles = {
  display: "grid",
  gridTemplateRows: "auto 1fr",
  gridTemplateColumns: "100%",
  borderRadius: "12px",
  overflow: "hidden",
  position: "relative",
  height: "100%",
} as const;

const BannerDiv = styled("div")(bannerRootStyles);
const BannerAnchor = styled("a")(bannerRootStyles);
// Typed-router variant of the banner root (typed TanStack Links migration):
// same migrated root styles, router-driven navigation.
const BannerRouterLink = styled(RouterLink)(bannerRootStyles);

// Migrated from CMSBanner.less (.scha__banner__background-container), with the
// .forced-ratio-container mixin expanded. Padding ratios are exact LESS
// expressions (9/16 mobile, 1/3.35 from tablet up).
const BackgroundContainer = styled("div")(({ theme }) => ({
  gridRow: 1,
  gridColumn: 1,
  width: "100%",
  position: "relative",
  paddingTop: "56.25%",
  [theme.breakpoints.up("tablet")]: { paddingTop: "29.850746268656716%" },
  "& > *": { position: "absolute", top: 0, left: 0, right: 0, bottom: 0 },
}));

// Migrated from CMSBanner.less (.scha__banner__background).
const Background = styled("div")({
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
});

// Migrated from CMSBanner.less (.scha__banner__content).
const BannerContent = styled("div")(({ theme }) => ({
  gridRow: 2,
  gridColumn: 1,
  background: C_SURFACE,
  padding: "16px",
  borderLeft: `${C_PRIMARY} 22px solid`,
  [theme.breakpoints.up("tablet")]: {
    gridRow: 1,
    position: "absolute",
    left: 0,
    bottom: "13.333333%",
    borderLeftWidth: "30px",
    // Transparency as taken from https://www.schaeffler.de/content.schaeffler.de/de/index.jsp
    background: "rgba(255, 255, 255, 0.9)",
  },
}));

// Migrated from CMSBanner.less (.scha__banner__headline). The LESS
// `__headline + __headline` sibling override only ever hit the subheadline
// (which carried both classes); it is folded into BannerSubheadline below.
const BannerHeadline = styled("p")(({ theme }) => ({
  ...getFontStyle(FontSize.TitleLarge),
  margin: 0,
  color: C_ON_SURFACE,
  overflow: "hidden",
  textOverflow: "ellipsis",
  [theme.breakpoints.up("desktop-s")]: { ...getFontStyle(FontSize.HeadlineLarge) },
}));

// Migrated from CMSBanner.less (.scha__banner__subheadline). Uses the
// canonical C_ON_SURFACE_VARIANT token (GREY_40): SCHAEFF-39396 deliberately
// moved the token GREY_30 -> GREY_40 on 2026-05-29; the LESS sheet stayed
// stale, so this migration applies the corrected color (coordinator ruling
// 2026-09-06).
const BannerSubheadline = styled("p")({
  ...getFontStyle(FontSize.TitleLarge),
  margin: "2px 0 0",
  color: C_ON_SURFACE_VARIANT,
  overflow: "unset",
  whiteSpace: "unset",
});

// Migrated from CMSBanner.less (.scha__banner__link), carried onto the
// consolidated MUI-facade Link primitive (button semantics preserved).
const BannerLink = styled(Link)(({ theme }) => ({
  ...getFontStyle(FontSize.TitleMedium),
  marginTop: "8px",
  [theme.breakpoints.up("desktop-s")]: { ...getFontStyle(FontSize.TitleLarge) },
}));

export interface CMSBannerProps {
  type: ComponentTypes.CMSBanner;
  headlineTag?: string;
  headline?: string;
  subheadline?: string;
  linkUrl?: string;
  linkLabel?: string;
  imageUrl?: string;
  mobileImageUrl?: string;
  className?: string;
  target?: "newWindow" | "sameWindow";
}

export const CMSBanner = ({
  headline,
  headlineTag,
  subheadline,
  imageUrl,
  mobileImageUrl,
  linkLabel,
  linkUrl,
  className,
  target,
}: CMSBannerProps) => {
  const screenSize = useNumericScreenSize();
  const isDesktopMode = screenSize > useNumericScreenSize.phone;

  headline = cleanupText(headline);
  subheadline = cleanupText(subheadline);
  linkLabel = cleanupText(linkLabel);

  const showContentBox = !!(headline || subheadline || linkLabel);
  const Headline = (headlineTag?.trim() ?? "p") as keyof JSX.IntrinsicElements;
  // Legacy LESS hook class (test/e2e selector surface), carried across the
  // styled-components migration like the pre-migration markup.
  const cls = classNames("scha__banner", className);

  const getAspectRatio = () => {
    if (!isDesktopMode) {
      return showContentBox ? "auto" : "16 / 9";
    }
    return "3.35 / 1";
  };

  const bgImageStyle = useMemo(() => {
    const bgImageUrl = pickImage([mobileImageUrl, imageUrl], isDesktopMode);
    if (bgImageUrl) {
      return {
        backgroundImage: `url(${bgImageUrl})`,
        aspectRatio: getAspectRatio(),
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- INTENTIONAL-STALE: getAspectRatio is a render-pure ratio helper over CMS content that is static per slot
  }, [imageUrl, mobileImageUrl, isDesktopMode]);

  const content = (
    <>
      {bgImageStyle && (
        <BackgroundContainer>
          <Background style={bgImageStyle} />
        </BackgroundContainer>
      )}
      {showContentBox && (
        <BannerContent>
          {headline && <BannerHeadline as={Headline}>{headline}</BannerHeadline>}
          {subheadline && <BannerSubheadline>{subheadline}</BannerSubheadline>}
          {linkLabel && (
            <BannerLink
              component="button"
              type="button"
              startIcon={<KeyboardArrowRightIcon className={"scha__link--arrow-link"} sx={{ fontSize: 20 }} />}
            >
              {linkLabel}
            </BannerLink>
          )}
        </BannerContent>
      )}
    </>
  );

  if (linkUrl) {
    // CMS urls are free-form authorable: site-relative self-targeting urls navigate through
    // the router; external urls and deliberate new tabs stay raw anchors (HEAD-exact).
    const typedTo = isSiteRelativeUrl(linkUrl) && target !== "newWindow" ? linkUrl : undefined;
    return typedTo ? (
      <BannerRouterLink to={typedTo} className={cls} onClick={() => trackBannerClick(linkUrl)}>
        {content}
      </BannerRouterLink>
    ) : (
      <BannerAnchor
        href={resolveUrl(linkUrl)}
        className={cls}
        target={target === "newWindow" ? "_blank" : undefined}
        onClick={() => trackBannerClick(linkUrl)}
      >
        {content}
      </BannerAnchor>
    );
  } else {
    return <BannerDiv className={cls}>{content}</BannerDiv>;
  }
};

const pickImage = (images: (string | undefined)[], isDesktop: boolean) => {
  let image: string | undefined;
  const index = isDesktop ? 1 : 0;
  image = images[index];
  if (!image) {
    image = images.filter((url) => !!url)[0];
  }
  return image;
};
